/**
 * The mesh policy as people edit it: the two standing rules as switches, a table of grants (who
 * may reach what, on which ports), the tests a policy must pass, and a check that shows the test
 * results and who gains or loses reach before anything is published. The JSON stays behind
 * "View as JSON", read-only.
 */

import { MESH_PORT_SPEC_RE, MESH_TRANSPORT_DEFAULT, type MeshGrant, type MeshNode, type MeshPolicy, type MeshPolicyCheck, type MeshPolicyTest, type MeshPostureRequirement, type MeshTransportMode } from '@aioe/shared';
import { Copy, ListCheck, Shield, ShieldCheck, TrashBin } from '@gravity-ui/icons';
import { Segment } from '@heroui-pro/react';
import { Popover, Radio, RadioGroup, Tooltip } from '@heroui/react';
import { useMemo, useState } from 'react';

import { ApiError, problemText, routes } from '../api/client.ts';
import { DefinitionsPanel, RemoteControlPanel } from './meshAccess.tsx';
import { SelectorChip, SelectorList } from './meshSelectors.tsx';
import { requirementsInWords } from './meshWords.ts';
import { Button, ErrorNote, inputClass, ListBlock, ListGroup, StatusMark, Tick, Toggle, type ArtTone } from './ui.tsx';

interface Row {
  key: number;
  grant: MeshGrant;
  /** The ports as typed, kept while they do not parse. */
  portsText: string;
}

let seq = 0;
const rowOf = (grant: MeshGrant): Row => ({ key: ++seq, grant, portsText: portsToText(grant.ports ?? []) });

export function portsToText(ports: readonly string[]): string {
  return ports.length === 0 || ports.includes('*') ? '' : ports.join(', ');
}

/** `22, 8080-8090` -> specs; empty or "every port" -> every port; undefined when something does not parse. */
export function parsePorts(text: string): string[] | undefined {
  const t = text.trim().toLowerCase();
  if (!t || t === '*' || t === 'every port' || t === 'all') return [];
  const parts = t.split(/[\s,]+/).filter(Boolean);
  if (!parts.every((p) => MESH_PORT_SPEC_RE.test(p))) return undefined;
  if (!parts.every((p) => p.split('-').every((n) => Number(n) >= 1 && Number(n) <= 65535))) return undefined;
  return parts.includes('*') ? [] : [...new Set(parts)];
}

/** Ports in words: every port, port 22, ports 22 and 8080-8090. */
export function portsInWords(ports: 'none' | readonly string[] | undefined): string {
  if (ports === 'none') return 'no port';
  if (!ports || ports.length === 0 || ports.includes('*')) return 'every port';
  if (ports.length === 1 && ports[0] === '0') return 'no port';
  return `${ports.length === 1 && !ports[0]!.includes('-') ? 'port' : 'ports'} ${ports.join(', ')}`;
}

/** The editor's starting point: the stored policy with version 1 tag rules read as grants. */
function draftOf(p: MeshPolicy): { policy: MeshPolicy; rows: Row[] } {
  const fromRules: MeshGrant[] = (p.rules ?? []).map((r) => ({ ...(r.name ? { name: r.name } : {}), src: r.fromTags.map((t) => `tag:${t}`), dst: r.toTags.map((t) => `tag:${t}`), ports: r.ports.map(String) }));
  return { policy: { ...p, version: 2, rules: [], grants: [], tests: p.tests ?? [] }, rows: [...fromRules, ...(p.grants ?? [])].map(rowOf) };
}

function assemble(policy: MeshPolicy, rows: Row[]): MeshPolicy {
  return { ...policy, version: 2, rules: [], grants: rows.map((r) => r.grant) };
}

/** A requirement left empty is no requirement: drop it, and drop the whole block when nothing is left. */
function tidy(r: MeshPostureRequirement): MeshPostureRequirement | undefined {
  const minOs = Object.fromEntries(Object.entries(r.minOsVersion ?? {}).filter(([, v]) => v.trim()));
  const out: MeshPostureRequirement = {
    ...(r.diskEncrypted ? { diskEncrypted: true } : {}),
    ...(r.firewall ? { firewall: true } : {}),
    ...(r.screenLock ? { screenLock: true } : {}),
    ...(r.patchedWithinDays ? { patchedWithinDays: r.patchedWithinDays } : {}),
    ...(Object.keys(minOs).length ? { minOsVersion: minOs } : {}),
  };
  return Object.keys(out).length ? out : undefined;
}

const OS_FIELDS = [
  ['windows', 'Windows', '10.0.22631'],
  ['darwin', 'macOS', '14.0'],
  ['linux', 'Linux', '6.1'],
] as const;

/** What a grant asks of the machine it starts from: a shield that opens the choices. */
function RequiresPicker({ value, onChange }: { value: MeshPostureRequirement | undefined; onChange: (v: MeshPostureRequirement | undefined) => void }) {
  const [open, setOpen] = useState(false);
  const r = value ?? {};
  const set = (patch: Partial<MeshPostureRequirement>) => onChange(tidy({ ...r, ...patch }));
  const on = requirementsInWords(value).length > 0;
  const [days, setDays] = useState(r.patchedWithinDays ? String(r.patchedWithinDays) : '');
  return (
    <Popover isOpen={open} onOpenChange={setOpen}>
      <Tooltip delay={300}>
        <Button size="sm" variant="ghost" isIconOnly aria-label="What this grant requires of the machine">
          {on ? <ShieldCheck className="size-4 text-accent" /> : <Shield className="size-4" />}
        </Button>
        <Tooltip.Content>{on ? 'Requires device posture' : 'Require device posture'}</Tooltip.Content>
      </Tooltip>
      <Popover.Content placement="bottom end" className="w-80 max-w-[calc(100vw-1rem)]">
        <Popover.Dialog className="flex flex-col gap-3 p-3" aria-label="What this grant requires of the machine">
          <p className="text-sm text-muted">The grant applies only to machines that report all of these. Workbenches report them when they register.</p>
          <div className="grid gap-1.5 text-sm">
            <Tick checked={!!r.diskEncrypted} onChange={(v) => set({ diskEncrypted: v || undefined })}>Disk encryption</Tick>
            <Tick checked={!!r.firewall} onChange={(v) => set({ firewall: v || undefined })}>Firewall on</Tick>
            <Tick checked={!!r.screenLock} onChange={(v) => set({ screenLock: v || undefined })}>Screen lock</Tick>
          </div>
          <label className="flex items-center justify-between gap-3 text-sm">
            <span>Patched within</span>
            <span className="flex items-center gap-2">
              <input
                type="number"
                min={1}
                max={365}
                className={`${inputClass} !w-20 text-right tabular-nums`}
                aria-label="Patched within days"
                placeholder="any"
                value={days}
                onChange={(e) => {
                  setDays(e.target.value);
                  const n = Number(e.target.value);
                  set({ patchedWithinDays: Number.isInteger(n) && n >= 1 && n <= 365 ? n : undefined });
                }}
              />
              <span className="text-muted">days</span>
            </span>
          </label>
          <div className="grid gap-1.5 text-sm">
            <span>Minimum operating system</span>
            {OS_FIELDS.map(([os, label, example]) => (
              <label key={os} className="flex items-center justify-between gap-3">
                <span className="text-muted">{label}</span>
                <input
                  className={`${inputClass} mono !w-36`}
                  aria-label={`Minimum ${label} version`}
                  placeholder={example}
                  value={r.minOsVersion?.[os] ?? ''}
                  onChange={(e) => set({ minOsVersion: { ...r.minOsVersion, [os]: e.target.value.replace(/[^0-9.]/g, '') } })}
                />
              </label>
            ))}
          </div>
        </Popover.Dialog>
      </Popover.Content>
    </Popover>
  );
}

/** One reach change as a sentence. */
function changeText(c: MeshPolicyCheck['changes'][number]): { verb: string; rest: string } {
  if (c.was === 'none') return { verb: 'gains', rest: `on ${portsInWords(c.now)}` };
  if (c.now === 'none') return { verb: 'loses', rest: '' };
  return { verb: 'now reaches', rest: `on ${portsInWords(c.now)} (was ${portsInWords(c.was)})` };
}

/** Remount it (a `key`) when the stored policy changes: the draft starts from it once. */
/** Which part of the policy the editor shows: Access controls' tabs, and Settings' device management. */
export type PolicySection = 'rules' | 'remote' | 'approvers' | 'tests' | 'definitions' | 'json' | 'device';

/**
 * One draft serves every section (the page keeps this mounted and says which part to show), so a
 * change made under one tab is still there under another, and one publish bar covers them all.
 */
export function MeshPolicyEditor({ policy, nodes, tone, section, onPublished }: { policy: MeshPolicy; nodes: readonly MeshNode[]; tone: ArtTone; section?: PolicySection; onPublished: () => void }) {
  const start = useMemo(() => draftOf(policy), [policy]);
  const [draft, setDraft] = useState(start.policy);
  const [rows, setRows] = useState(start.rows);
  const [check, setCheck] = useState<MeshPolicyCheck>();
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState<'check' | 'publish'>();

  const next = assemble(draft, rows);
  const dirty = JSON.stringify(next) !== JSON.stringify(assemble(start.policy, start.rows));
  const badPorts = rows.some((r) => parsePorts(r.portsText) === undefined);
  const emptyGrant = rows.some((r) => r.grant.src.length === 0 || r.grant.dst.length === 0);
  const invalid = badPorts ? 'A grant has ports that are not a port, a range like 8080-8090, or empty for every port.' : emptyGrant ? 'Every grant needs someone on each side: add who, and what they may reach.' : undefined;

  const edit = (f: (p: MeshPolicy) => MeshPolicy) => {
    setDraft(f);
    setCheck(undefined);
  };
  const editRows = (f: (r: Row[]) => Row[]) => {
    setRows(f);
    setCheck(undefined);
  };
  const setGrant = (key: number, g: Partial<MeshGrant>, portsText?: string) => editRows((rs) => rs.map((r) => (r.key === key ? { ...r, grant: { ...r.grant, ...g }, ...(portsText !== undefined ? { portsText } : {}) } : r)));

  async function runCheck() {
    setError(undefined);
    setBusy('check');
    try {
      setCheck(await routes.checkMeshPolicy(next));
    } catch (e) {
      setError(problemText(e));
    } finally {
      setBusy(undefined);
    }
  }

  async function publish() {
    setError(undefined);
    setBusy('publish');
    try {
      await routes.saveMeshPolicy(next);
      setCheck(undefined);
      onPublished();
    } catch (e) {
      if (e instanceof ApiError && e.code === 'policy_tests_failed') {
        const tests = (e.body as { tests: MeshPolicyCheck['tests'] }).tests;
        // The reach preview too, so the refusal shows what the policy would have changed.
        setCheck(await routes.checkMeshPolicy(next).catch(() => undefined));
        setError(`Not published: ${tests.filter((t) => !t.pass).length} of the policy's tests ${tests.filter((t) => !t.pass).length === 1 ? 'fails' : 'fail'}. Change the grants or the tests and try again.`);
      } else setError(problemText(e));
    } finally {
      setBusy(undefined);
    }
  }

  if (!section) return null;
  return (
    <>
      {section === 'rules' && (
      <ListGroup title="General access rules" count={rows.length + (draft.ownerReachesOwnNodes ? 1 : 0) + (draft.operatorsReachAll ? 1 : 0)} description="A rule lets machines reach others on the ports it names; each machine refuses the rest."><ListBlock flush>
        <ul className="divide-y divide-separator text-sm" role="list">
          <StandingRule title="Owners reach their own nodes" body="Each person's machines reach one another on every port." checked={draft.ownerReachesOwnNodes} onChange={(v) => edit((p) => ({ ...p, ownerReachesOwnNodes: v }))} />
          <StandingRule title="Operators reach everything" body="Operators' and administrators' machines, and the platform gateway, reach every node on every port." checked={draft.operatorsReachAll} onChange={(v) => edit((p) => ({ ...p, operatorsReachAll: v }))} />
          <li className="hidden grid-cols-[minmax(0,1fr)_minmax(0,1fr)_14rem] gap-4 px-4 pb-1 pt-3 text-xs text-muted md:grid">
            <span>Sources</span>
            <span>Destinations</span>
            <span>Ports</span>
          </li>
          {rows.map((r) => {
            const bad = parsePorts(r.portsText) === undefined;
            return (
              <li key={r.key} className="grid gap-2 px-4 py-2.5 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_14rem] md:items-center md:gap-4">
                <div className="flex min-w-0 items-baseline gap-2">
                  <span className="w-20 shrink-0 text-xs text-muted md:hidden">Who</span>
                  <SelectorList side="src" value={r.grant.src} nodes={nodes} label="Add who" onChange={(src) => setGrant(r.key, { src })} />
                </div>
                <div className="flex min-w-0 items-baseline gap-2">
                  <span className="w-20 shrink-0 text-xs text-muted md:hidden">May reach</span>
                  <SelectorList side="dst" value={r.grant.dst} nodes={nodes} label="Add what they may reach" onChange={(dst) => setGrant(r.key, { dst })} />
                </div>
                <div className="flex min-w-0 items-center gap-2">
                  <span className="w-20 shrink-0 text-xs text-muted md:hidden">On ports</span>
                  <input
                    className={`${inputClass} mono min-w-0 flex-1 ${bad ? '!border-danger' : ''}`}
                    value={r.portsText}
                    placeholder="every port"
                    aria-label="On ports"
                    aria-invalid={bad}
                    title={bad ? 'A port (22), a range (8080-8090), several with commas, or empty for every port' : undefined}
                    onChange={(e) => {
                      const ports = parsePorts(e.target.value);
                      setGrant(r.key, ports ? { ports } : {}, e.target.value);
                    }}
                  />
                  <RequiresPicker value={r.grant.srcPosture} onChange={(srcPosture) => setGrant(r.key, { srcPosture })} />
                  <Tooltip delay={300}>
                    <Button size="sm" variant="ghost" isIconOnly aria-label="Remove this grant" onPress={() => editRows((rs) => rs.filter((x) => x.key !== r.key))}>
                      <TrashBin className="size-4" />
                    </Button>
                    <Tooltip.Content>Remove this grant</Tooltip.Content>
                  </Tooltip>
                </div>
                <details className="md:col-span-3">
                  <summary className="w-fit cursor-pointer text-xs text-muted hover:text-foreground">Show as JSON</summary>
                  <pre className="mono mt-1 overflow-x-auto rounded-lg bg-surface-secondary p-2 text-xs">{JSON.stringify(r.grant, null, 2)}</pre>
                </details>
                {requirementsInWords(r.grant.srcPosture).length > 0 && (
                  <p className="text-xs text-muted md:col-span-3">
                    <span className="text-foreground">Requires</span> {requirementsInWords(r.grant.srcPosture).join(', ')} of the machine it starts from; a machine that does not report it is left out.
                  </p>
                )}
              </li>
            );
          })}
        </ul>
        <div className="flex flex-wrap items-center gap-2 border-t border-separator px-4 py-3">
          <Button size="sm" variant="secondary" onPress={() => editRows((rs) => [...rs, rowOf({ src: [], dst: [], ports: [] })])}>
            Add a grant
          </Button>
          {rows.length === 0 && <span className="text-xs text-muted">Only the rules above apply until a grant connects other nodes.</span>}
        </div>
      </ListBlock></ListGroup>
      )}

      {section === 'remote' && <RemoteControlPanel tone={tone} draft={draft} grants={rows.map((r) => r.grant)} nodes={nodes} />}

      {section === 'tests' && <TestsPanel tone={tone} tests={draft.tests ?? []} nodes={nodes} check={check} onChange={(tests) => edit((p) => ({ ...p, tests }))} />}

      {section === 'definitions' && <DefinitionsPanel tone={tone} draft={draft} grants={rows.map((r) => r.grant)} nodes={nodes} />}

      {section === 'json' && <JsonPanel tone={tone} policy={next} />}

      {section === 'device' && <TransportPanel tone={tone} mode={draft.transport?.mode ?? MESH_TRANSPORT_DEFAULT} onChange={(mode) => edit((p) => ({ ...p, transport: { mode } }))} />}

      {(section === 'approvers' || section === 'device') && <KeysAndTags tone={tone} policy={draft} part={section === 'device' ? 'lifetime' : 'owners'} onChange={edit} />}

      {check && <CheckPanel tone={tone} check={check} />}

      <div className="flex flex-wrap items-center gap-2">
        <Button variant="secondary" isPending={busy === 'check'} isDisabled={!!invalid} onPress={() => void runCheck()}>
          <ListCheck className="size-4" />
          Check
        </Button>
        <Button variant="primary" isPending={busy === 'publish'} isDisabled={!dirty || !!invalid} onPress={() => void publish()}>
          Publish
        </Button>
        {dirty && (
          <Button
            variant="ghost"
            onPress={() => {
              setDraft(start.policy);
              setRows(start.rows);
              setCheck(undefined);
              setError(undefined);
            }}
          >
            Discard changes
          </Button>
        )}
        <span className="text-xs text-muted">{invalid ?? (dirty ? 'Check shows the tests and who gains or loses reach before you publish.' : 'This is the policy in force.')}</span>
      </div>
      <ErrorNote error={error} />

    </>
  );
}

function StandingRule({ title, body, checked, onChange }: { title: string; body: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <li className="flex items-center justify-between gap-4 px-4 py-2.5">
      <span className="min-w-0">
        <span className="block font-medium">{title}</span>
        <span className="block text-xs text-muted">{body}</span>
      </span>
      <Toggle checked={checked} onChange={onChange} label={title} />
    </li>
  );
}

/** The tests: sentences, and a short form that adds one. */
function TestsPanel({ tone: _tone, tests, nodes, check, onChange }: { tone: ArtTone; tests: MeshPolicyTest[]; nodes: readonly MeshNode[]; check?: MeshPolicyCheck; onChange: (t: MeshPolicyTest[]) => void }) {
  const [src, setSrc] = useState<string[]>([]);
  const [dst, setDst] = useState<string[]>([]);
  const [allow, setAllow] = useState(true);
  const [port, setPort] = useState('');
  const portNum = port.trim() ? Number(port.trim()) : undefined;
  const portBad = portNum !== undefined && !(Number.isInteger(portNum) && portNum >= 1 && portNum <= 65535);
  const add = () => {
    onChange([...tests, { src: src[0]!, dst: dst[0]!, allow, ...(portNum !== undefined ? { port: portNum } : {}) }]);
    setSrc([]);
    setDst([]);
    setPort('');
  };
  return (
    <ListGroup title="Tests" count={tests.length} description="Checked on every publish: a policy that fails one is refused."><ListBlock flush>
      <ul className="divide-y divide-separator text-sm" role="list">
        {tests.map((t, i) => {
          const result = check?.tests[i];
          return (
            <li key={`${t.src}/${t.dst}/${t.port ?? ''}/${t.allow}/${i}`} className="flex items-center gap-3 px-4 py-2">
              <span className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
                <SelectorChip sel={t.src} nodes={nodes} />
                <span className={t.allow ? '' : 'font-medium'}>{t.allow ? 'can reach' : 'cannot reach'}</span>
                <SelectorChip sel={t.dst} nodes={nodes} />
                {t.port !== undefined && <span className="text-muted">on port {t.port}</span>}
              </span>
              {result && (
                <span className="shrink-0 text-right">
                  <StatusMark state={result.pass ? 'connected' : 'error'} label={result.pass ? 'Passes' : 'Fails'} />
                  {result.reason && <span className="block max-w-56 truncate text-xs text-muted" title={result.reason}>{result.reason}</span>}
                </span>
              )}
              <Tooltip delay={300}>
                <Button size="sm" variant="ghost" isIconOnly aria-label="Remove this test" onPress={() => onChange(tests.filter((_, j) => j !== i))}>
                  <TrashBin className="size-4" />
                </Button>
                <Tooltip.Content>Remove this test</Tooltip.Content>
              </Tooltip>
            </li>
          );
        })}
        {tests.length === 0 && <li className="px-4 py-2.5 text-muted">No tests yet. A test says, for example, that phones cannot reach the build servers, and stops a policy that would let them.</li>}
      </ul>
      <div className="grid gap-2 border-t border-separator px-4 py-3">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <SelectorList side="src" value={src} nodes={nodes} single label="Choose who" onChange={setSrc} />
          <Segment aria-label="Can or cannot" size="sm" selectedKey={allow ? 'can' : 'cannot'} onSelectionChange={(k) => setAllow(String(k) === 'can')} className="w-fit">
            <Segment.Item id="can">can reach</Segment.Item>
            <Segment.Item id="cannot">cannot reach</Segment.Item>
          </Segment>
          <SelectorList side="dst" value={dst} nodes={nodes} single label="Choose what" onChange={setDst} />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input className={`${inputClass} mono !w-36 ${portBad ? '!border-danger' : ''}`} placeholder="any port" aria-label="On port" value={port} onChange={(e) => setPort(e.target.value)} />
          <Button size="sm" variant="secondary" isDisabled={!src.length || !dst.length || portBad} onPress={add}>
            Add a test
          </Button>
        </div>
      </div>
    </ListBlock></ListGroup>
  );
}

/** The three ways mesh traffic may travel, in words; the first is the default. */
export const TRANSPORT_MODES: ReadonlyArray<readonly [MeshTransportMode, string, string]> = [
  ['relay-https', 'Through the relay over HTTPS', "Every machine sends its mesh traffic through your organisation's relay as ordinary HTTPS, so it passes any firewall or proxy that lets the web through. Nothing travels directly between machines."],
  ['relay', 'Through the relay', 'Every machine sends its mesh traffic through the relay, over a WebSocket where the network allows one and HTTPS where it does not. Nothing travels directly between machines.'],
  ['auto', 'Directly where possible', 'Machines connect to each other directly when their networks allow it, which is fastest, and use the relay only when they cannot.'],
];

/** How traffic travels: forced through the relay over HTTPS (the default), through the relay, or directly where possible. */
function TransportPanel({ tone: _tone, mode, onChange }: { tone: ArtTone; mode: MeshTransportMode; onChange: (m: MeshTransportMode) => void }) {
  return (
    <ListGroup title="How traffic travels" description="Traffic is encrypted end to end whichever way it goes; the relay never sees inside it."><ListBlock>
      <RadioGroup aria-label="How traffic travels" value={mode} onChange={(v) => onChange(v as MeshTransportMode)} className="grid gap-3">
        {TRANSPORT_MODES.map(([id, title, body]) => (
          <Radio key={id} value={id}>
            <Radio.Content className="items-start font-normal">
              <Radio.Control className="mt-0.5">
                <Radio.Indicator />
              </Radio.Control>
              <span className="min-w-0">
                <span className="block font-medium">
                  {title}
                  {id === MESH_TRANSPORT_DEFAULT && <span className="font-normal text-muted"> (default)</span>}
                </span>
                <span className="block text-xs text-muted">{body}</span>
              </span>
            </Radio.Content>
          </Radio>
        ))}
      </RadioGroup>
      <p className="mt-3 text-xs text-muted">A mesh agent from before this setting does not know it. While traffic goes through the relay, the platform gives it no direct address to try, so it uses the relay too, but it may still reach the relay over a WebSocket.</p>
    </ListBlock></ListGroup>
  );
}

/** Who may give each tag, and how long keys last. */
function KeysAndTags({ tone: _tone, policy, part, onChange }: { tone: ArtTone; policy: MeshPolicy; part: 'lifetime' | 'owners'; onChange: (f: (p: MeshPolicy) => MeshPolicy) => void }) {
  const [tag, setTag] = useState('');
  const owners = policy.tagOwners ?? {};
  const set = (t: string, who: 'operator' | 'approver', on: boolean) =>
    onChange((p) => {
      const cur = new Set(p.tagOwners?.[t] ?? []);
      if (on) cur.add(who);
      else cur.delete(who);
      return { ...p, tagOwners: { ...p.tagOwners, [t]: [...cur] } };
    });
  const tagOk = /^[a-z0-9][a-z0-9-]{0,39}$/.test(tag.trim().replace(/^tag:/, ''));
  return (
    <ListGroup title={part === 'lifetime' ? 'Key expiry' : 'Auto approvers'} description={part === 'lifetime' ? 'How long a machine\u2019s key lasts before its agent rotates it.' : 'Who may give each tag without an administrator. A tag nobody is listed for is for administrators only.'}><ListBlock flush>
      <ul className="divide-y divide-separator text-sm" role="list">
        {part === 'lifetime' && (
          <>
        <li className="flex flex-wrap items-center justify-between gap-3 px-4 py-2.5">
          <span>
            <span className="block font-medium">Key lifetime</span>
            <span className="block text-xs text-muted">Days a node's key lasts before its agent must rotate it; 0 never expires.</span>
          </span>
          <input
            type="number"
            min={0}
            max={3650}
            className={`${inputClass} !w-24 text-right tabular-nums`}
            aria-label="Key lifetime in days"
            value={policy.keyLifetimeDays}
            onChange={(e) => {
              const n = Math.max(0, Math.min(3650, Math.floor(Number(e.target.value) || 0)));
              onChange((p) => ({ ...p, keyLifetimeDays: n }));
            }}
          />
        </li>
        <li className="flex items-center justify-between gap-4 px-4 py-2.5">
          <span className="min-w-0">
            <span className="block font-medium">Administrators may turn key expiry off for one machine</span>
            <span className="block text-xs text-muted">For a server that must not drop off the mesh. Off puts every such machine back on the lifetime above.</span>
          </span>
          <Toggle checked={policy.keyExpiryOffAllowed !== false} onChange={(v) => onChange((p) => ({ ...p, keyExpiryOffAllowed: v ? undefined : false }))} label="Administrators may turn key expiry off for one machine" />
        </li>
        <li className="px-4 py-2.5 text-xs text-muted">
          New machines join with a join key or wait for approval (Nodes); a machine that asks for tags waits for someone listed under Auto approvers. Device posture is required per rule, under General access rules.
        </li>
          </>
        )}
        {part === 'owners' && Object.keys(owners)
          .sort()
          .map((t) => (
            <li key={t} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2">
              <span className="mono basis-full sm:flex-1 sm:basis-auto">{t}</span>
              <Tick checked={owners[t]!.includes('operator')} onChange={(v) => set(t, 'operator', v)}>
                Operators may give it
              </Tick>
              <Tick checked={owners[t]!.includes('approver')} onChange={(v) => set(t, 'approver', v)}>
                Node approvers may give it
              </Tick>
              <Tooltip delay={300}>
                <Button
                  size="sm"
                  variant="ghost"
                  isIconOnly
                  aria-label={`Administrators only for ${t}`}
                  onPress={() =>
                    onChange((p) => {
                      const { [t]: _, ...rest } = p.tagOwners ?? {};
                      return { ...p, tagOwners: rest };
                    })
                  }
                >
                  <TrashBin className="size-4" />
                </Button>
                <Tooltip.Content>Administrators only</Tooltip.Content>
              </Tooltip>
            </li>
          ))}
      </ul>
      {part === 'owners' && (
      <div className="flex flex-wrap items-center gap-2 border-t border-separator px-4 py-3">
        <input className={`${inputClass} mono !w-44`} placeholder="a tag, such as gpu" aria-label="Tag" value={tag} onChange={(e) => setTag(e.target.value)} />
        <Button
          size="sm"
          variant="secondary"
          isDisabled={!tagOk || owners[tag.trim().replace(/^tag:/, '')] !== undefined}
          onPress={() => {
            const t = tag.trim().replace(/^tag:/, '');
            onChange((p) => ({ ...p, tagOwners: { ...p.tagOwners, [t]: ['operator'] } }));
            setTag('');
          }}
        >
          Let others give a tag
        </Button>
      </div>
      )}
    </ListBlock></ListGroup>
  );
}

/** The check's answer: how the tests fared and who gains or loses reach. */
function CheckPanel({ tone: _tone, check }: { tone: ArtTone; check: MeshPolicyCheck }) {
  const failed = check.tests.filter((t) => !t.pass).length;
  return (
    <ListGroup title="What changes" count={check.changes.length} description={check.tests.length ? (failed ? `${failed} of ${check.tests.length} tests fail` : `All ${check.tests.length} tests pass`) : 'No tests to run'}><ListBlock flush>
      <ul className="divide-y divide-separator text-sm" role="list">
        {check.changes.map((c) => {
          const t = changeText(c);
          return (
            <li key={`${c.from.id}>${c.to.id}`} className="px-4 py-2">
              <span className="font-medium">{c.from.name}</span> <span className={t.verb === 'loses' ? 'text-danger' : 'text-accent'}>{t.verb}</span> <span className="font-medium">{c.to.name}</span>
              {t.rest && <span className="text-muted"> {t.rest}</span>}
              {c.reason === 'posture' && <span className="block text-xs text-muted">Because of the machine's posture{c.posture?.length ? `: ${c.posture.map((f) => f.message).join('; ')}` : ''}</span>}
            </li>
          );
        })}
        {check.changes.length === 0 && <li className="px-4 py-2.5 text-muted">Nobody gains or loses reach: the nodes on the mesh now would see the same peers on the same ports.</li>}
      </ul>
    </ListBlock></ListGroup>
  );
}

function JsonPanel({ tone: _tone, policy }: { tone: ArtTone; policy: MeshPolicy }) {
  const text = JSON.stringify(policy, null, 2);
  const [copied, setCopied] = useState(false);
  return (
    <ListGroup title="JSON" description="What Publish would send, including changes not yet published. Read-only: edit the policy under the other tabs." actions={
        <Button
          size="sm"
          variant="secondary"
          onPress={() =>
            void navigator.clipboard.writeText(text).then(() => {
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            })
          }
        >
          <Copy className="size-4" />
          {copied ? 'Copied' : 'Copy'}
        </Button>
      }><ListBlock>
      <pre className="mono max-h-[32rem] overflow-auto rounded-lg bg-surface-secondary p-3 text-xs leading-relaxed">{text}</pre>
    </ListBlock></ListGroup>
  );
}
